export function formatPrice(amount, currency = "GBP", locale = "en-GB") {
    if (amount === null || amount === undefined || Number.isNaN(+amount)) return "";
    if (!Number(amount)) return "Complimentary";
    try {
        return new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
    } catch {
        return `£${Number(amount).toFixed(0)}`;
    }
}

/**
 * Format a MONEY AMOUNT (never "Complimentary"). Use this everywhere the
 * value represents an actual sum of money paid or owed — e.g. "Amount paid",
 * "Outstanding balance", payment records. formatPrice() is only for a
 * treatment's headline price, where £0 legitimately means "complimentary".
 */
export function formatMoney(amount, currency = "GBP", locale = "en-GB") {
    const n = Number(amount);
    if (Number.isNaN(n)) return "£0.00";
    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(n);
    } catch {
        return `£${n.toFixed(2)}`;
    }
}

/**
 * Consistent payment-method label used everywhere the appointment payment
 * gateway is displayed — top summary card, PaymentPanel, receipts.
 *
 * Rules (matches the "Payment summary consistency" spec):
 *   - If payment_status === 'paid', show the real Amelia gateway
 *     capitalised (e.g. 'Stripe').
 *   - Otherwise, if gateway is missing OR is 'onSite' (Amelia's default),
 *     show '—'. We NEVER surface 'On-site' pre-payment, so it can never
 *     stay 'On-site' after an online payment has cleared.
 */
export function paymentMethodLabel(gateway, paymentStatus) {
    const s = (paymentStatus || "").toString().toLowerCase();
    const g = (gateway || "").toString();
    if (s === "paid") return g ? capitaliseGateway(g) : "Paid";
    if (!g || g === "onSite") return "—";
    return capitaliseGateway(g);
}

function capitaliseGateway(g) {
    if (g === "onSite") return "On-site";
    if (!g) return "";
    return g[0].toUpperCase() + g.slice(1);
}

export function formatDuration(seconds) {
    if (!seconds) return "";
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
}

export function formatFullDate(dateStr) {
    // dateStr = "YYYY-MM-DD"
    if (!dateStr) return "";
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export function formatShortDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

export function toISODate(d) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

/**
 * Compute the deposit due at booking-time from the Amelia service payload.
 * Amelia stores per-service:
 *   - deposit         (number)
 *   - depositPayment  ('fixed' | 'percentage' | 'disabled' | null)
 * We return { depositDue, remaining } in the same currency as service.price.
 * Nothing is hard-coded — everything comes from Amelia.
 */
export function computeDeposit(service) {
    if (!service) return { depositDue: 0, remaining: 0, hasDeposit: false };
    const price = Number(service.price) || 0;
    const mode = service.depositPayment;
    const raw = Number(service.deposit) || 0;
    if (!mode || mode === "disabled" || raw <= 0) {
        return { depositDue: 0, remaining: price, hasDeposit: false };
    }
    let depositDue = 0;
    if (mode === "fixed") depositDue = Math.min(raw, price);
    else if (mode === "percentage") depositDue = Math.round(price * (raw / 100) * 100) / 100;
    else depositDue = Math.min(raw, price);
    const remaining = Math.max(price - depositDue, 0);
    return { depositDue, remaining, hasDeposit: depositDue > 0 };
}
